import React, { useState, useEffect } from 'react';
import { X, Save, FileSignature } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { PropostaHeader } from '../components/proposta/PropostaHeader';
import { PropostaItems } from '../components/proposta/PropostaItems';
import { PropostaConditions } from '../components/proposta/PropostaConditions';
import { fixEncodingIssues } from '../utils/textUtils';
import { propostaMetaService } from '../services/propostaMetaService';
import { getClientesApenasList } from '../services/pessoaService';

interface PropostaFormProps {
  pedidoId?: number | null;
  onClose: () => void;
  onSave: () => void;
}

export function PropostaForm({ pedidoId, onClose, onSave }: PropostaFormProps) {
  const [formData, setFormData] = useState({
    pessoa_id: '',
    status: 'Rascunho',
    consideracoes: '',
    condicoes_comerciais: '',
    condicoes_pagamento: '',
    total_geral: 0
  });
  const [items, setItems] = useState<any[]>([]);
  const [clientes, setClientes] = useState<any[]>([]);
  const [servicos, setServicos] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchLookups();
    if (pedidoId) fetchPedido();
  }, [pedidoId]);

  const fetchLookups = async (currentClienteId?: number | string) => {
    const c = await getClientesApenasList(currentClienteId || formData.pessoa_id);
    const { data: s } = await supabase.from('tipo_servico').select('id, tipo').order('tipo');
    setClientes(c || []);
    setServicos((s || []).map((srv: any) => ({ ...srv, tipo: fixEncodingIssues(srv.tipo) })));
  };

  const fetchPedido = async () => {
    const { data: pedido } = await supabase
      .from('pedido')
      .select('*, detalhes_do_pedido(*)')
      .eq('id', pedidoId)
      .single();
    
    if (pedido) {
      // Garante que a lista de clientes contenha o cliente deste pedido
      if (pedido.pessoa_id) {
        fetchLookups(pedido.pessoa_id);
      }
      const rawDetails = pedido.detalhes_do_pedido || [];
      const { items: cleanDetails, meta: dbMeta } = propostaMetaService.separateItemsAndMeta(rawDetails);
      const fetchedMeta = await propostaMetaService.getMeta(pedidoId!, rawDetails);

      const loadedItems = cleanDetails.map((item: any) => ({
        ...item,
        servicos: fixEncodingIssues(item.servicos || ''),
        quantidade: Number(item.quantidade || 0),
        valor_unitario: Number(item.valor_unitario || 0),
        valor_total: Number(item.valor_total) || (Number(item.quantidade || 0) * Number(item.valor_unitario || 0))
      }));

      const finalStatus = fetchedMeta.status || dbMeta.status || pedido.status || 'Rascunho';
      const finalConsideracoes = fetchedMeta.consideracoes || dbMeta.consideracoes || pedido.consideracoes || '';
      const finalComerciais = fetchedMeta.condicoes_comerciais || dbMeta.condicoes_comerciais || pedido.condicoes_comerciais || '';
      const finalPagamento = fetchedMeta.condicoes_pagamento || dbMeta.condicoes_pagamento || pedido.condicoes_pagamento || '';
      const finalTotal = fetchedMeta.total_geral || dbMeta.total_geral || pedido.total_geral || loadedItems.reduce((acc: number, i: any) => acc + i.valor_total, 0);

      setFormData({
        pessoa_id: pedido.pessoa_id,
        status: finalStatus,
        consideracoes: finalConsideracoes,
        condicoes_comerciais: finalComerciais,
        condicoes_pagamento: finalPagamento,
        total_geral: finalTotal
      });
      setItems(loadedItems);
    }
  };

  // Função resiliente para salvar o pedido no Supabase ignorando colunas não existentes na tabela 'pedido'
  const savePedidoWithFallback = async (payload: any, targetPedidoId?: number | null) => {
    let currentPayload = { ...payload };

    while (Object.keys(currentPayload).length > 0) {
      if (targetPedidoId) {
        const { data, error } = await supabase
          .from('pedido')
          .update(currentPayload)
          .eq('id', targetPedidoId)
          .select();

        if (!error) return { id: targetPedidoId, data };

        const msg = error.message || '';
        const missingColMatch = msg.match(/Could not find the '([^']+)' column of 'pedido'/) ||
                                msg.match(/column pedido\.([^\s]+) does not exist/);

        if (missingColMatch && missingColMatch[1]) {
          const colToRemove = missingColMatch[1];
          console.warn(`Coluna '${colToRemove}' ausente na tabela 'pedido'. Removendo do payload.`);
          delete currentPayload[colToRemove];
          continue;
        }

        // Se falhar por qualquer outro motivo com campos opcionais, faz strip progressivo
        if ('consideracoes' in currentPayload) { delete currentPayload.consideracoes; continue; }
        if ('condicoes_comerciais' in currentPayload) { delete currentPayload.condicoes_comerciais; continue; }
        if ('condicoes_pagamento' in currentPayload) { delete currentPayload.condicoes_pagamento; continue; }
        if ('total_geral' in currentPayload) { delete currentPayload.total_geral; continue; }
        if ('status' in currentPayload) { delete currentPayload.status; continue; }

        throw error;
      } else {
        const { data, error } = await supabase
          .from('pedido')
          .insert(currentPayload)
          .select()
          .single();

        if (!error && data) return data;

        const msg = error?.message || '';
        const missingColMatch = msg.match(/Could not find the '([^']+)' column of 'pedido'/) ||
                                msg.match(/column pedido\.([^\s]+) does not exist/);

        if (missingColMatch && missingColMatch[1]) {
          const colToRemove = missingColMatch[1];
          console.warn(`Coluna '${colToRemove}' ausente na tabela 'pedido'. Removendo do payload.`);
          delete currentPayload[colToRemove];
          continue;
        }

        if ('consideracoes' in currentPayload) { delete currentPayload.consideracoes; continue; }
        if ('condicoes_comerciais' in currentPayload) { delete currentPayload.condicoes_comerciais; continue; }
        if ('condicoes_pagamento' in currentPayload) { delete currentPayload.condicoes_pagamento; continue; }
        if ('total_geral' in currentPayload) { delete currentPayload.total_geral; continue; }
        if ('status' in currentPayload) { delete currentPayload.status; continue; }

        throw error;
      }
    }

    // Último recurso: envia apenas o ID e pessoa_id
    if (targetPedidoId) {
      const { data, error } = await supabase.from('pedido').update({ pessoa_id: payload.pessoa_id }).eq('id', targetPedidoId).select();
      if (error) throw new Error('Erro ao atualizar pedido: ' + error.message);
      return { id: targetPedidoId, data };
    } else {
      const { data, error } = await supabase.from('pedido').insert({ pessoa_id: payload.pessoa_id }).select().single();
      if (error) throw new Error('Erro ao criar pedido: ' + error.message);
      return data;
    }
  };

  const handleSave = async () => {
    if (!formData.pessoa_id) {
      alert("Selecione um cliente (Pessoa) antes de salvar a proposta.");
      return;
    }
    setIsLoading(true);
    try {
      const validItems = items.filter(item => item.tipo_servico_id);
      const itemsToInsert = validItems.map(item => {
        const qtd = parseFloat(item.quantidade) || 0;
        const unit = parseFloat(item.valor_unitario) || 0;
        const itemTotal = parseFloat(item.valor_total) || (qtd * unit);
        return {
          id: item.id || crypto.randomUUID(),
          tipo_servico_id: parseInt(item.tipo_servico_id.toString(), 10),
          servicos: fixEncodingIssues(item.servicos || ''),
          quantidade: qtd,
          valor_unitario: unit,
          valor_total: itemTotal
        };
      });

      const totalCalculado = itemsToInsert.reduce((acc, i) => acc + i.valor_total, 0);
      let pedidoIdToUse = pedidoId;

      const basePayload: any = {
        pessoa_id: formData.pessoa_id,
        status: formData.status || 'Rascunho',
        consideracoes: formData.consideracoes || '',
        condicoes_comerciais: formData.condicoes_comerciais || '',
        condicoes_pagamento: formData.condicoes_pagamento || '',
        total_geral: totalCalculado
      };

      const savedPedido = await savePedidoWithFallback(basePayload, pedidoId);
      if (!pedidoIdToUse && savedPedido?.id) {
        pedidoIdToUse = savedPedido.id;
      }

      if (pedidoId) {
        // Buscar detalhes atualmente existentes no banco de dados para sincronização
        const { data: existingItemsDb, error: fetchError } = await supabase
          .from('detalhes_do_pedido')
          .select('id')
          .eq('pedido_id', pedidoId);
          
        if (fetchError) throw new Error('Erro ao buscar detalhes existentes: ' + fetchError.message);
        
        const currentIds = itemsToInsert.map(i => i.id);
        const dbIds = (existingItemsDb || []).map(i => i.id).filter(id => typeof id === 'string' && !id.startsWith('meta_pedido_'));
        const removedIds = dbIds.filter(id => !currentIds.includes(id));
        
        if (removedIds.length > 0) {
          // Verificar se algum item removido está vinculado a uma OS (ordem_de_servico)
          const { data: linkedServico, error: errServico } = await supabase
            .from('ordem_de_servico')
            .select('id')
            .in('item_servico', removedIds);
            
          const { data: linkedAjudante, error: errAjudante } = await supabase
            .from('ordem_de_servico')
            .select('id')
            .in('item_ajudante', removedIds);
            
          if (errServico || errAjudante) {
            throw new Error('Erro ao verificar dependências dos itens da proposta.');
          }
            
          const hasLinked = (linkedServico && linkedServico.length > 0) || (linkedAjudante && linkedAjudante.length > 0);
          if (hasLinked) {
            throw new Error('Não é possível salvar a proposta: Um ou mais itens removidos já estão vinculados a Ordens de Serviço existentes. Remova o vínculo das OSs primeiro.');
          }
          
          // Se nenhum estiver vinculado, remove com segurança
          const { error: deleteError } = await supabase
            .from('detalhes_do_pedido')
            .delete()
            .in('id', removedIds);
          if (deleteError) throw new Error('Erro ao atualizar detalhes (remoção): ' + deleteError.message);
        }
      }

      if (itemsToInsert.length > 0 && pedidoIdToUse) {
        const finalItems = itemsToInsert.map(i => ({ ...i, pedido_id: pedidoIdToUse }));
        // Upsert atualiza os existentes (com o mesmo ID) e insere os novos com segurança
        const { error: upsertError } = await supabase.from('detalhes_do_pedido').upsert(finalItems);
        if (upsertError) {
          console.error("Erro ao fazer upsert em detalhes_do_pedido:", upsertError);
          throw new Error('Falha ao salvar itens da proposta: ' + upsertError.message);
        }
      }

      // Salvar metadados da proposta (status, considerações, condições comerciais e pagamento) de forma garantida
      if (pedidoIdToUse) {
        const defaultServicoId = itemsToInsert[0]?.tipo_servico_id || 1;
        await propostaMetaService.saveMeta(
          pedidoIdToUse,
          {
            status: formData.status || 'Rascunho',
            consideracoes: formData.consideracoes || '',
            condicoes_comerciais: formData.condicoes_comerciais || '',
            condicoes_pagamento: formData.condicoes_pagamento || '',
            total_geral: totalCalculado
          },
          defaultServicoId
        );
      }
      
      onSave();
    } catch (error: any) {
      console.error("Erro ao salvar proposta:", error);
      alert("Ocorreu um erro ao salvar a proposta: " + (error.message || error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex justify-center items-start pt-2 pb-2 sm:pt-10 sm:pb-10 overflow-y-auto">
      <div className="bg-slate-50 w-full max-w-5xl rounded-xl sm:rounded-2xl shadow-2xl flex flex-col relative min-h-[85vh] mx-2 sm:mx-4">
        
        {/* Header Fixo */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 sm:px-6 py-3.5 sm:py-4 rounded-t-xl sm:rounded-t-2xl flex justify-between items-center shadow-xs">
          <div className="flex items-center gap-3">
            <div className="bg-red-50 p-2 sm:p-2.5 rounded-xl border border-red-100 shrink-0">
              <FileSignature className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-base sm:text-xl font-bold text-slate-800 tracking-tight leading-tight">
                {pedidoId ? 'Editar Proposta Comercial' : 'Nova Proposta Comercial'}
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 font-medium mt-0.5">
                Preencha os dados abaixo para gerar o contrato
              </p>
            </div>
          </div>
          <button 
            type="button"
            onClick={onClose}
            className="p-1.5 sm:p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors cursor-pointer"
            title="Fechar"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Corpo do Formulário */}
        <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 flex-1 overflow-y-auto">
          <PropostaHeader formData={formData} setFormData={setFormData} clientes={clientes} />
          <PropostaItems items={items} setItems={setItems} servicos={servicos} />
          <PropostaConditions formData={formData} setFormData={setFormData} />
        </div>

        {/* Footer Fixo */}
        <div className="sticky bottom-0 z-10 bg-white border-t border-slate-200 px-4 sm:px-6 py-3 sm:py-4 rounded-b-xl sm:rounded-b-2xl flex flex-col-reverse sm:flex-row justify-end gap-2.5 sm:gap-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
          <button 
            type="button"
            onClick={onClose} 
            className="w-full sm:w-auto px-5 py-2.5 text-xs sm:text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:text-slate-800 transition-colors cursor-pointer text-center"
          >
            Cancelar
          </button>
          <button 
            type="button"
            onClick={handleSave} 
            disabled={isLoading}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 text-xs sm:text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 shadow-sm cursor-pointer"
          >
            <Save size={16} />
            {isLoading ? 'Salvando...' : 'Salvar Proposta'}
          </button>
        </div>

      </div>
    </div>
  );
}
